import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Clock, ArrowLeft, ChevronDown, CheckCircle, Users, MessageSquare, Calendar, Scale, Search, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { categories } from '../data/categories';
import { normalizeSlug, getAvailability } from '../utils/availability';
import { getApiUrl } from '../config/api';


const badges = {
  'top-match': { label: 'TOP MATCH', bg: 'bg-primary', text: 'text-white' },
  'highly-rated': { label: 'HIGHLY RATED', bg: 'bg-accent-gold', text: 'text-white' },
  'available-now': { label: 'AVAILABLE NOW', bg: 'bg-accent-blue', text: 'text-white' },
};

const TopMatchCard = ({ advisor, badge }) => {
  const badgeConfig = badges[badge];
  const name = advisor.first_name && advisor.last_name 
    ? `${advisor.first_name} ${advisor.last_name}`
    : advisor.name || 'Advisor';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
  const rating = advisor.rating || advisor.rating_average || 0;
  const location = advisor.location || (advisor.location_city ? `${advisor.location_city}, ${advisor.location_state}` : null);
  
  return (
    <div className="bg-white rounded-xl border border-border-light p-5 hover:shadow-card-hover transition-shadow">
      {badgeConfig && (
        <div className={`inline-block ${badgeConfig.bg} ${badgeConfig.text} text-xs font-semibold px-2 py-1 rounded mb-3`}>
          {badgeConfig.label}
        </div>
      )}
      
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: '#004D35' }}>
          {advisor.profile_photo_url ? (
            <img src={advisor.profile_photo_url} alt={name} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <span className="font-semibold" style={{ color: '#fff' }}>{initials}</span>
          )}
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">{name}</h3>
          <p className="text-sm text-text-secondary">
            {advisor.category_name || 'Advisor'}
            {advisor.years_experience && ` • ${advisor.years_experience} yrs`}
          </p>
        </div>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        {location && (
          <div className="flex items-center gap-2 text-text-secondary">
            <MapPin className="w-4 h-4" />
            <span>{location}</span>
          </div>
        )}
        {advisor.hourly_rate && (
          <div className="flex items-center gap-2 text-text-secondary">
            <span className="font-medium">${advisor.hourly_rate}/hr</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-success">
          <Clock className="w-4 h-4" />
          <span className="text-sm">{getAvailability(normalizeSlug(advisor.availability_status)).label}</span>
        </div>
      </div>
      
      <Link
        to={`/advisor/${advisor.profile_slug || advisor.id}`}
        className="block w-full text-center bg-bg-secondary hover:bg-border-light text-text-primary font-medium py-2 rounded-lg transition-colors"
      >
        View Profile
      </Link>
    </div>
  );
};

const AdvisorCard = ({ advisor }) => {
  const name = advisor.first_name && advisor.last_name 
    ? `${advisor.first_name} ${advisor.last_name}`
    : advisor.name || 'Advisor';
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
  const rating = advisor.rating || advisor.rating_average || 0;
  const location = advisor.location || (advisor.location_city ? `${advisor.location_city}, ${advisor.location_state}` : null);
  
  return (
    <div className="bg-white rounded-xl border border-border-light p-4 md:p-5 hover:shadow-card-hover transition-shadow">
      <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ background: '#004D35' }}>
          {advisor.profile_photo_url ? (
            <img src={advisor.profile_photo_url} alt={name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className="font-semibold text-base md:text-lg" style={{ color: '#fff' }}>{initials}</span>
          )}
        </div>
        
        <div className="flex-1 min-w-0 w-full">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
            <div>
              <h3 className="font-semibold text-text-primary">{name}</h3>
              <p className="text-sm text-text-secondary">
                {advisor.category_name || 'Advisor'}
                {advisor.years_experience && ` • ${advisor.years_experience} years experience`}
              </p>
            </div>
            
            <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-0 sm:text-right flex-shrink-0 mt-1 sm:mt-0">
              {advisor.hourly_rate && (
                <div className="font-semibold text-text-primary">${advisor.hourly_rate}/hr</div>
              )}
              <div className="flex items-center gap-1 text-success text-sm sm:mt-1">
                <Clock className="w-3 h-3" />
                <span>{getAvailability(normalizeSlug(advisor.availability_status)).label}</span>
              </div>
            </div>
          </div>
          
          {advisor.headline && (
            <p className="text-text-secondary mt-3 text-sm line-clamp-2">
              "{advisor.headline}"
            </p>
          )}
          
          <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-3 text-sm text-text-secondary">
            {location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{location}</span>
              </div>
            )}
            {advisor.remote_available && (
              <span className="text-primary">Remote Available</span>
            )}
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4">
            <Link
              to={`/advisor/${advisor.profile_slug || advisor.id}`}
              className="bg-primary hover:bg-primary-hover text-white font-medium px-4 py-2.5 sm:py-2 rounded-lg transition-colors text-sm text-center"
            >
              View Profile
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsNextSection = ({ advisorCount, advisors, isAuthenticated, signIn }) => {
  const handleViewProfiles = () => {
    const advisorCards = document.getElementById('advisor-list');
    if (advisorCards) {
      advisorCards.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const steps = [
    {
      icon: Users,
      title: "View advisor profiles",
      description: "Read reviews, check credentials, and find the best fit",
      onClick: handleViewProfiles
    },
    {
      icon: Calendar,
      title: "Book a free consultation",
      description: "Most advisors offer a free intro call"
    },
    {
      icon: MessageSquare,
      title: "Send a message",
      description: "Ask questions before committing"
    }
  ];
  
  return (
    <div className="bg-white rounded-xl border border-border-light p-4 md:p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-text-primary">What's Next?</h2>
      </div>
      <p className="text-text-secondary text-sm mb-4">
        You've found {advisorCount} advisor{advisorCount !== 1 ? 's' : ''} that match your needs. Here's how to move forward:
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {steps.map((step, index) => {
          const CardWrapper = step.onClick ? 'button' : 'div';
          return (
            <CardWrapper 
              key={index} 
              onClick={step.onClick}
              className={`flex flex-col items-center text-center p-4 rounded-lg bg-bg-secondary ${step.onClick ? 'hover:bg-border-light cursor-pointer' : ''} transition-colors`}
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <step.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-medium text-text-primary mb-1">{step.title}</h3>
              <p className="text-xs text-text-secondary">{step.description}</p>
            </CardWrapper>
          );
        })}
      </div>
      
      {advisorCount >= 2 && (
        <div className="mt-4 p-3 bg-accent-gold/10 rounded-lg flex items-center gap-3">
          <Scale className="w-5 h-5 text-accent-gold flex-shrink-0" />
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">Tip:</span> Review multiple advisor profiles to find the best fit for your business needs
          </p>
        </div>
      )}
      
      {!isAuthenticated && (
        <div className="mt-4 p-4 bg-primary/5 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-text-secondary">
            <span className="font-medium text-text-primary">Create a free account</span> to save your matches and message advisors
          </p>
          <button
            onClick={signIn}
            className="bg-primary hover:bg-primary-hover text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm whitespace-nowrap"
          >
            Sign Up Free
          </button>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ category, categoryInfo }) => {
  const navigate = useNavigate();
  
  return (
    <div className="text-center py-12 bg-white rounded-xl border border-border-light">
      <div className="w-16 h-16 bg-bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
        <Search className="w-8 h-8 text-text-muted" />
      </div>
      <h3 className="text-lg font-semibold text-text-primary mb-2">
        No {categoryInfo?.label || 'advisors'} found matching your criteria
      </h3>
      <p className="text-text-secondary mb-6 max-w-md mx-auto">
        Don't worry! We're always adding new advisors. Try adjusting your search or explore other categories.
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-medium px-5 py-2.5 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Try Different Answers
        </Link>
        <Link
          to="/browse"
          className="inline-flex items-center gap-2 text-primary hover:text-primary-hover font-medium px-5 py-2.5 rounded-lg border border-primary hover:bg-primary/5 transition-colors"
        >
          <Scale className="w-4 h-4" />
          Browse All Advisors
        </Link>
      </div>
      
      <div className="mt-8 pt-6 border-t border-border-light">
        <p className="text-sm text-text-muted mb-3">Explore other advisor types:</p>
        <div className="flex flex-wrap justify-center gap-2">
          {categories.filter(c => c.id !== category).slice(0, 4).map(cat => (
            <Link
              key={cat.id}
              to={`/advisors/${cat.id}`}
              className="px-3 py-1.5 text-sm bg-bg-secondary hover:bg-border-light text-text-secondary rounded-full transition-colors"
            >
              {cat.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const AdvisorResults = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, signIn, signOut } = useAuth();
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('match');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const categoryInfo = categories.find(c => c.id === category);

  useEffect(() => {
    const fetchAdvisors = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams(searchParams);
        params.set('category', category);
        
        const response = await fetch(`${getApiUrl()}/api/advisors/search?${params.toString()}`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdvisors(data.advisors || []);
        } else {
          setAdvisors([]);
        }
      } catch (error) {
        console.error('Error fetching advisors:', error);
        setAdvisors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvisors();
  }, [category, searchParams]);

  const topMatches = advisors.filter(a => a.badge).slice(0, 3);
  const allAdvisors = advisors;

  return (
    <div className="min-h-screen bg-bg-secondary">
      <nav className="bg-white border-b border-border-light sticky top-0 z-40">
        <div className="max-w-site mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-primary">Viysr</span>
            </Link>

            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated && user ? (
                <Link
                  to={user.role === 'admin' ? '/admin' : user.role === 'advisor' ? '/advisor-portal' : '/dashboard'}
                  className="text-text-secondary hover:text-text-primary font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <button
                  onClick={signIn}
                  className="bg-primary hover:bg-primary-hover text-white font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  Sign up
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-site mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1 text-text-secondary hover:text-text-primary mb-4">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to search</span>
          </Link>
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h1 className="text-xl md:text-2xl font-bold text-text-primary">
              Showing {advisors.length} {categoryInfo?.label || 'Advisors'} in San Diego
            </h1>
            
            <div className="relative w-full sm:w-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none bg-white border border-border-light rounded-lg px-4 py-2.5 pr-10 text-text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 w-full sm:w-auto"
              >
                <option value="match">Best Match</option>
                <option value="rating">Highest Rated</option>
                <option value="reviews">Most Reviews</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
            <p className="text-text-muted mt-4">Finding the best matches...</p>
          </div>
        ) : allAdvisors.length === 0 ? (
          <EmptyState category={category} categoryInfo={categoryInfo} />
        ) : (
          <>
            <WhatsNextSection 
              advisorCount={allAdvisors.length}
              advisors={allAdvisors}
              isAuthenticated={isAuthenticated} 
              signIn={signIn}
            />
            
            {topMatches.length > 0 && (
              <div className="mb-6 md:mb-8">
                <div className="bg-white rounded-xl border border-border-light p-4 md:p-6">
                  <h2 className="text-base md:text-lg font-semibold text-text-primary mb-1">YOUR TOP MATCHES</h2>
                  <p className="text-text-secondary text-sm mb-4">Based on your business needs</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                    {topMatches.map((advisor) => (
                      <TopMatchCard key={advisor.id} advisor={advisor} badge={advisor.badge} />
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div id="advisor-list">
              <h2 className="text-lg font-semibold text-text-primary mb-4">ALL ADVISORS</h2>
              <div className="space-y-4">
                {allAdvisors.map((advisor) => (
                  <AdvisorCard key={advisor.id} advisor={advisor} />
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdvisorResults;
